import { useInfiniteQuery, UseInfiniteQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { z } from 'zod4';

import { useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';
import { useIdentityStore } from '@bonusly/who-am-i';

import { getCadenceToastText, getNextStartDate, getNextStartDateAsDate } from '../modules/team-management/utils/check-in-schedule';
import { CheckIn } from '../schemas/check-in';
import { Metadata, PaginatedData, PaginationParams } from './meta';

/*
 * Shared utilities
 */

enum QueryKey {
	LatestCheckIn = 'check-ins:latest',
	CheckInsInfinite = 'check-ins:infinite',
}

const headers = {
	'Key-Inflection': 'camel',
};

/*
 * Path and query key builders
 */

export const checkInsInfiniteQueryKey = (params?: Record<string, any>) => [QueryKey.CheckInsInfinite, params].filter(Boolean);
export const latestCheckInQueryKey = (params?: { today?: boolean }) => [QueryKey.LatestCheckIn, params].filter(Boolean);
export const meetingCheckInsQueryKey = (meetingId: string) => [QueryKey.LatestCheckIn, 'meeting', meetingId];

export const checkInsPath = (prefix = '') => `${prefix}/check_ins/check_ins`;
export const checkInPath = (id: string, prefix = '') => `${prefix}/check_ins/check_ins/${id}`;
export const latestCheckInPath = (prefix = '', params?: { today?: boolean }) => {
	const basePath = `${prefix}/check_ins/check_ins/latest`;
	if (params?.today) {
		return `${basePath}?today=true`;
	}
	return basePath;
};
export const meetingCheckInsPath = (meetingId: string, prefix = '') => `${prefix}/check_ins/check_ins/meeting?meeting_id=${meetingId}`;

export type CheckInAPIData = {
	id?: string;
	user_id: string;
	score: number;
	message: string;
	created_at: string;
	updated_at: string;
};

/*
 * Latest check-in query
 */

export const useLatestCheckIn = (params?: { today?: boolean }) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: latestCheckInQueryKey(params),
		queryFn: async () => {
			const res = await http.get<CheckInAPIData>(latestCheckInPath('', params), {
				headers,
			});
			// API returns a string or non-object when there's no check-in
			if (!res.data || typeof res.data !== 'object') {
				return null;
			}
			return CheckIn.parse(res.data);
		},
		retry: false, // Don't retry if no check-in exists
		refetchOnWindowFocus: false,
	});
};

/*
 * Meeting check-ins query
 */

type MeetingCheckInsResponse = {
	managerCheckIn: CheckInAPIData | null;
	directReportCheckIn: CheckInAPIData | null;
};

export const useMeetingCheckIns = (meetingId: string) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: meetingCheckInsQueryKey(meetingId),
		queryFn: async () => {
			const res = await http.get<MeetingCheckInsResponse>(meetingCheckInsPath(meetingId, ''), {
				headers,
			});

			const checkIns: CheckIn[] = [];

			if (res.data.managerCheckIn) {
				checkIns.push(CheckIn.parse(res.data.managerCheckIn));
			}

			if (res.data.directReportCheckIn) {
				checkIns.push(CheckIn.parse(res.data.directReportCheckIn));
			}

			return checkIns;
		},
		refetchOnWindowFocus: false,
	});
};

/*
 * Infinite scroll query for check-ins
 */

type InfiniteCheckInsParams = Omit<PaginationParams<{ period?: string }>, 'page_number'> & {
	period?: string;
};

export const useInfiniteCheckIns = (
	params?: InfiniteCheckInsParams
): UseInfiniteQueryResult<
	{
		records: CheckIn[];
		metadata: Metadata;
	},
	Error
> => {
	const http = useHttpClient();

	return useInfiniteQuery({
		queryKey: checkInsInfiniteQueryKey(params),
		queryFn: async ({ pageParam = 1 }) => {
			const res = await http.get<PaginatedData<CheckInAPIData>>(checkInsPath(), {
				params: {
					...params,
					page_number: pageParam,
				},
				headers,
			});

			// Filter out legacy records without createdAt before parsing
			const filteredRecords = res.data.records.filter((record: any) => record.createdAt !== null);
			const parsedRecords = z.array(CheckIn).parse(filteredRecords);

			return {
				records: parsedRecords,
				metadata: new Metadata(res.data.metadata),
			};
		},
		getNextPageParam: (lastPage) => {
			return lastPage.metadata.pageNext;
		},
		refetchOnWindowFocus: false,
	});
};

/*
 * Create/Update mutations
 */

type MutateCheckInAPIData = {
	score: number;
	message?: string;
};

type UpdateCheckInAPIData = {
	score?: number;
	message?: string;
	meetingId?: string;
};

type MutateCheckInOptions = {
	onError?: (error: unknown) => void;
	onMutate?: () => void;
	onSuccess?: (checkIn?: CheckIn) => void;
};

export const useCreateCheckIn = (options: MutateCheckInOptions = {}) => {
	const http = useHttpClient();
	const invalidateLatest = useInvalidateLatestCheckIn();
	const invalidateCheckIns = useInvalidateCheckIns();

	return useMutation({
		mutationFn: async (data: MutateCheckInAPIData) => {
			try {
				options.onMutate?.();

				const res = await http.post(checkInsPath(), data, { headers });
				const record = res.data;
				invalidateLatest();
				invalidateCheckIns();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useUpdateCheckIn = (id: string, options: MutateCheckInOptions = {}) => {
	const http = useHttpClient();
	const invalidateLatest = useInvalidateLatestCheckIn();
	const invalidateCheckIns = useInvalidateCheckIns();

	return useMutation({
		mutationFn: async (data: UpdateCheckInAPIData) => {
			try {
				options.onMutate?.();

				const res = await http.patch(checkInPath(id), { id, ...data }, { headers });
				const record = res.data;
				invalidateLatest();
				invalidateCheckIns();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Cache management
 */

export const useInvalidateLatestCheckIn = () => {
	const client = useQueryClient();

	return useCallback(() => {
		client.invalidateQueries({ queryKey: [QueryKey.LatestCheckIn] });
	}, [client]);
};

export const useInvalidateCheckIns = () => {
	const client = useQueryClient();

	return useCallback(() => {
		client.invalidateQueries({ queryKey: [QueryKey.CheckInsInfinite] });
	}, [client]);
};

export const useInvalidateMeetingCheckIns = (meetingId: string) => {
	const client = useQueryClient();

	return useCallback(() => {
		client.invalidateQueries({ queryKey: meetingCheckInsQueryKey(meetingId) });
	}, [client, meetingId]);
};

export const useInvalidateIdentity = () => {
	const refetchProfile = useIdentityStore((state) => state.refetchProfile);

	return useCallback(async () => {
		await refetchProfile();
	}, [refetchProfile]);
};

// More efficient: Only invalidate check-in related data
export const useInvalidateCompany = () => {
	const client = useQueryClient();

	return useCallback(() => {
		client.invalidateQueries({ queryKey: ['company'] });
		client.invalidateQueries({ queryKey: ['identity'] });
	}, [client]);
};

/*
 * Manager check-in settings
 */

interface ManagerCheckInSettings {
	cadence: string | null;
}

export const useManagerCheckInSettings = () => {
	const http = useHttpClient('v2');
	const toast = useToast();
	const invalidateIdentity = useInvalidateIdentity();
	const invalidateCompany = useInvalidateCompany();

	const updateSettingsMutation = useMutation(
		['update-manager-check-in-settings'],
		async (settings: ManagerCheckInSettings) => {
			const scheduledAtDate = getNextStartDateAsDate(settings.cadence || undefined);
			const scheduledAt = scheduledAtDate ? scheduledAtDate.toISOString() : null;

			if (settings.cadence === null) {
				return http.delete('check_ins/manager_settings');
			} else {
				const payload = {
					manager_check_in_settings: {
						cadence: settings.cadence,
						scheduled_at: scheduledAt,
					},
				};

				return http.put('check_ins/manager_settings', payload);
			}
		},
		{
			onSuccess: async (data, variables) => {
				await invalidateCompany();
				await invalidateIdentity();

				toast({
					position: 'top-right',
					duration: 5000,
					status: 'success',
					title: variables.cadence
						? `${getCadenceToastText(variables.cadence)} begin ${getNextStartDate(variables.cadence)}`
						: 'Check-ins have been stopped for your team',
				});
			},
			onError: (error) => {
				console.error('Failed to update manager check-in settings:', error);
				toast({
					title: 'Failed to update check-in settings. Please try again.',
					status: 'error',
				});
			},
		}
	);

	return {
		updateSettings: updateSettingsMutation.mutateAsync,
		isLoading: updateSettingsMutation.isLoading,
	};
};

type CompanyCheckInSettingsFormData = {
	mandatory: boolean | undefined;
	effectiveDate: Date | null;
	announce: boolean;
};

type CompanyCheckInSettingsResponse = {
	mandatory: boolean | null;
	announce: boolean | null;
	effective_date: string | null;
};

type MutateCompanyCheckInSettingsOptions = {
	onError?: (error: unknown) => void;
	onMutate?: () => void;
	onSuccess?: (data?: any) => void;
};

export const useCompanyCheckInSettings = () => {
	const http = useHttpClient('v2');

	return useQuery({
		queryKey: ['company-check-in-settings'],
		queryFn: async (): Promise<CompanyCheckInSettingsResponse> => {
			const response = await http.get('check_ins/company_settings');
			return response.data;
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

export const useUpdateCompanyCheckInSettings = (options: MutateCompanyCheckInSettingsOptions = {}) => {
	const http = useHttpClient('v2');
	const invalidateCompany = useInvalidateCompany();
	const client = useQueryClient();

	return useMutation({
		mutationFn: async (data: CompanyCheckInSettingsFormData) => {
			options.onMutate?.();
			try {
				const effectiveDateString = data.effectiveDate?.toISOString().split('T')[0] || null;

				const payload = {
					check_in_settings: {
						mandatory: data.mandatory,
						announce: data.announce && data.mandatory, // Set announce to true but allow unset if user changes the mandatory setting
						effective_date: effectiveDateString,
					},
				};

				const response = await http.put('check_ins/company_settings', payload);

				await Promise.all([client.invalidateQueries({ queryKey: ['company-check-in-settings'] }), invalidateCompany()]);

				options.onSuccess?.(response.data);
				return response.data;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};
